import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { FormTemplate } from '../types/formBuilder';
import { TemplateService } from '../services/templateService';
import { FormSubmissionService } from '../services/formSubmissionService';
import FormRenderer from '../components/forms/FormRenderer';
import LoadingScreen from '../components/common/LoadingScreen';
import LightModeWrapper from '../components/common/LightModeWrapper';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const EmbedFormFiller: React.FC = () => {
  const { publicId } = useParams<{ publicId: string }>();
  const { t } = useLanguage();

  const [template, setTemplate] = useState<FormTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (publicId) {
      loadPublicTemplate(publicId);
    } else {
      setError('Invalid form link');
      setIsLoading(false);
    }
  }, [publicId]);

  const loadPublicTemplate = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const publicTemplate = await TemplateService.getPublicFormTemplate(id);

      if (!publicTemplate) {
        setError('Form not found or no longer available');
        return;
      }

      // Check if form is still accepting submissions
      if (publicTemplate.publicSettings?.maxSubmissions) {
        const currentSubmissions = publicTemplate.publicSettings.currentSubmissions || 0;
        if (currentSubmissions >= publicTemplate.publicSettings.maxSubmissions) {
          setError('This form is no longer accepting submissions');
          return;
        }
      }

      // Check if form has expired
      if (publicTemplate.publicSettings?.expiresAt && 
          new Date() > new Date(publicTemplate.publicSettings.expiresAt)) {
        setError('This form has expired');
        return;
      }

      setTemplate(publicTemplate);
    } catch (error) {
      console.error('Error loading public template:', error);
      setError('Failed to load form. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = async (formData: Record<string, any>) => {
    if (!template) return;

    setIsSubmitting(true);
    try {
      // Create submission data
      const submissionData = {
        templateId: template.id,
        templateName: template.name,
        organizationId: template.organizationId,
        data: formData,
        submittedAt: new Date(),
        submittedBy: 'anonymous',
        submittedByName: 'Anonymous User',
        status: 'submitted' as const,
        isPublic: true
      };

      await FormSubmissionService.createSubmission(submissionData);

      // A beküldés-számlálót a szerver (automationOnFormSubmitted) növeli – anonim kliens nem írhatja a sablont

      setIsSubmitted(true);
      toast.success(t('formBuilder.publicForm.submitSuccess') || 'Form submitted successfully!');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('formFiller.submitError') || 'Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <LightModeWrapper className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </LightModeWrapper>
    );
  }

  if (error) {
    return (
      <LightModeWrapper className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            {t('formBuilder.publicForm.error') || 'Form Unavailable'}
          </h2>
          <p className="text-gray-600 mb-4 text-sm">
            {error}
          </p>
        </div>
      </LightModeWrapper>
    );
  }

  if (isSubmitted) {
    return (
      <LightModeWrapper className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            {t('formBuilder.publicForm.submitSuccess') || 'Form Submitted Successfully!'}
          </h2>
          <p className="text-gray-600 text-sm">
            {t('formBuilder.publicForm.submitSuccessMessage') || 'Thank you for your submission.'}
          </p>
        </div>
      </LightModeWrapper>
    );
  }

  if (!template) {
    return null;
  }

  return (
    <LightModeWrapper className="min-h-screen bg-gray-50">
      {/* Minimalist Embed Form */}
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="pt-6">
            <FormRenderer
              template={template}
              onSubmit={handleFormSubmit}
              isSubmitting={isSubmitting}
              showCancelButton={false}
              className="p-0"
            />
          </div>
        </div>
      </div>
    </LightModeWrapper>
  );
};

export default EmbedFormFiller;
